import axios from "axios";
import { normalizeCustomerID } from "@/classes/customerID";

// Leaf module — holds the URL/header config that entity APIs (BaseAPI,
// ClientAPI, …) need. Lives outside DataAPI.js so the entity APIs can
// reach the URL helpers without import-cycling through `../DataAPI` →
// `./APIs/index.js` → entity APIs.

// Bound every request so a stalled connection (e.g. a cold mobile network on
// first launch) can't hang forever — an un-timed request inside the post-login
// data load is what previously left users stuck on the login screen. Genuinely
// long-running endpoints (file uploads, Gotenberg PDF/label rendering, data
// export) opt out per-request with `timeout: 0`.
export const DEFAULT_REQUEST_TIMEOUT_MS = 30000;
// Guard `defaults` — it's always present in real axios, but a test that
// auto-mocks axios (`vi.mock("axios")`) leaves it undefined at module load.
if (axios.defaults) {
    axios.defaults.timeout = DEFAULT_REQUEST_TIMEOUT_MS;
    if (axios.defaults.headers?.common)
        axios.defaults.headers.common["X-Forkto-Capabilities"] = "inventory-volume-v1";
}

let baseUrl = import.meta.env.VITE_API_URL;
const apiRoute = "v1"; // Default API version
const apiRouteV2 = "v2"; // For migrated endpoints
let configuredCustomerID = normalizeCustomerID(
    axios.defaults?.headers?.common?.["Customer-ID"] || ""
);
const customerIDChangeListeners = new Set();

let isLoggingIn = true;

export function getAPIUrl(route) {
    return `${baseUrl}/${apiRoute}/${route}`;
}

export function getAPIUrlV2(route) {
    return `${baseUrl}/${apiRouteV2}/${route}`;
}

export function getBaseUrl() {
    return baseUrl;
}

export function getApiRouteV2() {
    return apiRouteV2;
}

export function setAPIBaseUrl(url) {
    baseUrl = url;
}

export function setAPICreds(customerID) {
    const nextCustomerID = normalizeCustomerID(customerID);
    const previousCustomerID = configuredCustomerID;

    setAPIBaseUrl(import.meta.env.VITE_API_URL);
    axios.defaults.headers.common["Customer-ID"] = nextCustomerID;
    configuredCustomerID = nextCustomerID;

    if (previousCustomerID !== nextCustomerID) {
        customerIDChangeListeners.forEach((listener) => {
            listener({ previousCustomerID, customerID: nextCustomerID });
        });
    }
}

/**
 * Register work that must happen when the API changes tenants.
 *
 * Customer-ID is part of the identity of every API response, even though it is
 * carried in a header rather than in the URL. Consumers such as Vue Query use
 * this boundary to discard data that belongs to the previous tenant.
 */
export function onAPICustomerIDChange(listener) {
    customerIDChangeListeners.add(listener);
    return () => customerIDChangeListeners.delete(listener);
}

export function setLoggingIn(value) {
    isLoggingIn = value;
}

export function getIsLoggingIn() {
    return isLoggingIn;
}

export function clearAxiosToken() {
    axios.defaults.headers.common["Authorization"] = "";
}

export function setAxiosToken(token) {
    axios.defaults.headers.common["Authorization"] = "Bearer " + token;
}
